import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { Check, Loader2, Minus } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

// ── Avatar ─────────────────────────────────────────────────────────────────

export function Avatar({
  src,
  alt,
  fallback,
  size = 'md',
  className,
}: {
  src?: string | null;
  alt: string;
  fallback: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const sizes = {
    xs: 'size-6 text-[10px]',
    sm: 'size-8 text-[11px]',
    md: 'size-10 text-[13px]',
    lg: 'size-14 text-base',
    xl: 'size-24 text-2xl',
  } as const;

  return (
    <AvatarPrimitive.Root
      className={cn(
        'relative flex shrink-0 overflow-hidden rounded-full bg-sunken ring-1 ring-line-strong/70',
        sizes[size],
        className,
      )}
    >
      {src ? (
        <AvatarPrimitive.Image src={src} alt={alt} className="aspect-square size-full object-cover" />
      ) : null}
      <AvatarPrimitive.Fallback
        delayMs={src ? 300 : 0}
        className="flex size-full items-center justify-center bg-sunken font-display font-medium text-accent-strong"
      >
        {fallback}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

// ── Switch ─────────────────────────────────────────────────────────────────

export const Switch = forwardRef<
  React.ComponentRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(function Switch({ className, ...props }, ref) {
  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        // Mobil ein Stück größer – ein 20px-Schalter ist mit dem Daumen
        // schwer zu erwischen.
        'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
        'sm:h-5 sm:w-9',
        'transition-colors duration-150',
        'data-[state=checked]:bg-brand data-[state=unchecked]:bg-line-strong',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-white shadow-sm sm:size-4',
          'transition-transform duration-150',
          'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
          'sm:data-[state=checked]:translate-x-4',
        )}
      />
    </SwitchPrimitive.Root>
  );
});

// ── Checkbox ───────────────────────────────────────────────────────────────

export const Checkbox = forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(function Checkbox({ className, ...props }, ref) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'flex size-5.5 shrink-0 items-center justify-center rounded-md border border-line-strong bg-surface sm:size-4.5 sm:rounded-[5px]',
        'transition-colors duration-150',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-brand-ink',
        'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand data-[state=indeterminate]:text-brand-ink',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {props.checked === 'indeterminate' ? (
          <Minus className="size-3.5 sm:size-3" strokeWidth={3} aria-hidden />
        ) : (
          <Check className="size-3.5 sm:size-3" strokeWidth={3} aria-hidden />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

// ── Tabs ───────────────────────────────────────────────────────────────────

export const Tabs = TabsPrimitive.Root;

export function TabsList({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <TabsPrimitive.List
      className={cn(
        // Umbrechen statt schieben: alle Reiter bleiben sichtbar, es gibt
        // keine versteckte Scrollleiste in der Leiste.
        //
        // Ohne Linie über die ganze Breite: der aktive Reiter trägt seinen
        // eigenen goldenen Strich, und eine durchlaufende Kante darunter
        // machte daraus wieder eine Registerlasche an einem Aktenordner.
        'flex flex-wrap items-baseline gap-x-5 gap-y-1',
        className,
      )}
    >
      {children}
    </TabsPrimitive.List>
  );
}

export function TabsTrigger({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'relative flex items-baseline gap-1.5 border-b-[1.5px] border-transparent',
        'pt-1 pb-1.5 sm:pb-1',
        'text-[14px] whitespace-nowrap text-ink-2 transition-colors sm:text-[13.5px]',
        'hover:text-ink',
        'data-[state=active]:border-accent data-[state=active]:font-medium data-[state=active]:text-ink',
        '[&_svg]:size-4 [&_svg]:self-center',
        className,
      )}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

export function TabsContent({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('pt-5 outline-none', className)} {...props} />;
}

// ── Tooltip ────────────────────────────────────────────────────────────────

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className={cn(
            'fb-pop z-[70] max-w-xs rounded-lg border border-line bg-surface px-2 py-1',
            'text-[12.5px] leading-snug text-ink shadow-pop',
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

// ── Segmentierter Umschalter ────────────────────────────────────────────────

export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  className,
  label,
}: {
  value: T;
  onValueChange: (value: T) => void;
  options: Array<{ value: T; label: React.ReactNode; title?: string }>;
  className?: string;
  label: string;
}) {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      aria-label={label}
      onValueChange={(next) => {
        if (next) onValueChange(next as T);
      }}
      className={cn(
        // Die Spur ist die *tiefere* Fläche, der gewählte Schalter liegt als
        // helle Karte darauf. Umgekehrt (helle Spur, getönter Schalter) liest
        // sich der gewählte Zustand als der ausgegraute.
        'inline-flex rounded-full bg-sunken p-[3px]',
        // Vier Schalter mit Beschriftung passen auf einem Handy nicht in eine
        // Zeile. `max-w-full` + eigenes Scrollen heißt: der Umschalter selbst
        // schiebt sich, nicht die ganze Seite. Ohne das ragte er auf
        // /fragen rechts heraus und machte die Ansicht verschiebbar.
        'max-w-full overflow-x-auto scroll-none',
        className,
      )}
    >
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          title={option.title}
          className={cn(
            // Feste Höhe statt Innenabstand: mit `py-*` hing die Höhe an der
            // Zeilenhöhe des Textes, und ein Umschalter mit Symbol war zwei
            // Pixel höher als einer mit Text. 36+6 bzw. 32+6 ergeben mit der
            // Spur außen 42 bzw. 38 – die `md`-Stufe.
            'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium',
            'sm:h-8 sm:px-3 sm:text-[13px]',
            'text-ink-2 transition-colors hover:text-ink',
            'data-[state=on]:bg-surface data-[state=on]:font-semibold data-[state=on]:text-ink data-[state=on]:shadow-xs',
            '[&_svg]:size-4',
          )}
        >
          {option.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}

// ── Kleinteile ─────────────────────────────────────────────────────────────

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin text-ink-3', className)} aria-hidden />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-sunken', className)} aria-hidden />;
}

export function Separator({ className }: { className?: string }) {
  return <div role="separator" className={cn('h-px w-full bg-line', className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      {icon ? (
        <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-accent-soft text-accent-strong [&_svg]:size-5">
          {icon}
        </div>
      ) : null}
      <p className="font-display text-[17px] font-medium text-ink">{title}</p>
      {description ? (
        <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-ink-3">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function InlineError({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] leading-relaxed text-danger"
    >
      {children}
    </div>
  );
}

/**
 * Wie `InlineError`, nur für Angaben, die sich widersprechen statt falsch zu
 * sein: das Speichern bleibt erlaubt, der Hinweis steht trotzdem da.
 */
export function InlineWarning({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  if (!children) return null;
  return (
    <div
      role="status"
      className={cn(
        'flex gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2',
        'text-[13px] leading-relaxed text-warn',
        '[&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
